/* eslint eslint-comments/no-use: off */
/* eslint-disable relay/unused-fields */
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import {PullRequestStateIcons} from '@github-ui/item-picker-base/constants/icons'
import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {VALUES} from '@github-ui/item-picker-base/constants/values'
import {type ExtendedItemProps, ItemPicker} from '@github-ui/item-picker-base/ItemPicker'
import {type ItemGroup, maybePullRequestUrl} from '@github-ui/item-picker-base/shared'
import {type BranchPickerBranch, searchBranchesQuery} from '@github-ui/item-picker-branch-relay/BranchPicker'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useDebounce} from '@github-ui/use-debounce'
import {GitBranchIcon, GitPullRequestIcon, TriangleDownIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {Octicon} from '@primer/styled-react/deprecated'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {fetchQuery, graphql, readInlineData, useFragment, useRelayEnvironment} from 'react-relay'

import type {
  PullRequestAndBranchPickerPullRequest$data,
  PullRequestAndBranchPickerPullRequest$key,
  PullRequestState,
} from './__generated__/PullRequestAndBranchPickerPullRequest.graphql'
import type {
  PullRequestAndBranchPickerQuery$data,
  PullRequestAndBranchPickerQuery$key,
} from './__generated__/PullRequestAndBranchPickerQuery.graphql'
import type {
  PullRequestAndBranchPickerSearchQuery,
  PullRequestAndBranchPickerSearchQuery$data,
} from './__generated__/PullRequestAndBranchPickerSearchQuery.graphql'
import styles from './PullRequestAndBranchPicker.module.css'

type PullRequestAndBranchPickerSharedProps = {
  onSelectionChange: (selected: Array<PullRequestPickerPullRequest | BranchPickerBranch>) => void
  shortcutsEnabled: boolean
  anchorElement?: (props: React.HTMLAttributes<HTMLElement>) => React.ReactElement
  subtitle?: string | React.ReactElement
  title?: string | React.ReactElement
  triggerOpen?: boolean
  onOpen?: () => void
  onClose?: () => void
  preventClose?: boolean
  loading?: boolean
}

export type PullRequestAndBranchPickerProps = PullRequestAndBranchPickerSharedProps & {
  repoNameWithOwner: string
  initialSelectedPrs: PullRequestPickerPullRequest[]
  initialSelectedBranches: BranchPickerBranch[]
  preventClose?: boolean
}

type PullRequestPickerInternalProps = PullRequestAndBranchPickerSharedProps & {
  pullRequestItemsKey: PullRequestAndBranchPickerQuery$key | null
  branches: BranchPickerBranch[]
  initialSelectedBranches?: BranchPickerBranch[]
  initialSelectedPrs?: PullRequestPickerPullRequest[]
  onFilter: (value: string) => void
}

export type PullRequestPickerBaseProps = Omit<PullRequestPickerInternalProps, 'pullRequestItemsKey' | 'branches'> & {
  branchItems: BranchPickerBranch[]
  pullRequestItems: PullRequestPickerPullRequest[]
}

// Export types for external consumption, following the pattern established in other picker components
export type PullRequestPickerPullRequest = Omit<PullRequestAndBranchPickerPullRequest$data, ' $fragmentType'>

// Export helper function for external readInlineData usage
export function readPullRequestFromFragment(
  key: PullRequestAndBranchPickerPullRequest$key,
): PullRequestPickerPullRequest {
  // eslint-disable-next-line no-restricted-syntax
  return readInlineData<PullRequestAndBranchPickerPullRequest$key>(PullRequestAndBranchPickerPullRequestFragment, key)
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const PullRequestAndBranchPickerPullRequestFragment = graphql`
  fragment PullRequestAndBranchPickerPullRequest on PullRequest @inline {
    id
    url
    number
    title
    state
    isDraft
    isInMergeQueue
    createdAt
    repository {
      id
      name
      nameWithOwner
      owner {
        id
        login
        __typename
      }
    }
    __typename
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const PullRequestAndBranchPickerQueryFragment = graphql`
  fragment PullRequestAndBranchPickerQuery on Query {
    commenters: search(type: ISSUE, first: $first, query: $commenters) {
      nodes {
        ...PullRequestAndBranchPickerPullRequest @dangerously_unaliased_fixme
      }
    }
    mentions: search(type: ISSUE, first: $first, query: $mentions) {
      nodes {
        ...PullRequestAndBranchPickerPullRequest @dangerously_unaliased_fixme
      }
    }
    assignee: search(type: ISSUE, first: $first, query: $assignee) {
      nodes {
        ...PullRequestAndBranchPickerPullRequest @dangerously_unaliased_fixme
      }
    }
    author: search(type: ISSUE, first: $first, query: $author) {
      nodes {
        ...PullRequestAndBranchPickerPullRequest @dangerously_unaliased_fixme
      }
    }
    open: search(type: ISSUE, first: $first, query: $open) {
      nodes {
        ...PullRequestAndBranchPickerPullRequest @dangerously_unaliased_fixme
      }
    }
    resource: resource(url: $resource) @include(if: $queryIsUrl) {
      ... on PullRequest {
        ...PullRequestAndBranchPickerPullRequest @dangerously_unaliased_fixme
      }
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const PullRequestAndBranchPickerSearchGraphQLQuery = graphql`
  query PullRequestAndBranchPickerSearchQuery(
    $commenters: String!
    $mentions: String!
    $assignee: String!
    $author: String!
    $open: String!
    $first: Int = 10
    $resource: URI!
    $queryIsUrl: Boolean!
  ) {
    ...PullRequestAndBranchPickerQuery
  }
`

export const PullRequestAndBranchPickerPlaceholder = () => (
  <Button
    leadingVisual={GitPullRequestIcon}
    trailingVisual={TriangleDownIcon}
    aria-labelledby="pr-picker-label"
    disabled
  >
    {LABELS.selectPr}
  </Button>
)

const selectedGroup: ItemGroup = {groupId: 'selected'}
const suggestionsGroup: ItemGroup = {groupId: 'suggestions', header: {title: 'Suggestions', variant: 'filled'}}

export const LazyPullRequestAndBranchPicker = ({
  repoNameWithOwner,
  initialSelectedPrs,
  initialSelectedBranches,
  loading,
  ...rest
}: PullRequestAndBranchPickerProps) => {
  const {addToast} = useToastContext()
  const relayEnvironment = useRelayEnvironment()
  const [pullRequestsQueryFragmentKey, setPullRequestsQueryFragmentKey] =
    useState<PullRequestAndBranchPickerQuery$key | null>(null)
  const [filter, setFilter] = useState('')
  const [searchLoading, setSearchLoading] = useState<boolean>(loading ?? false)

  const [searchResults, setSearchResults] = useState<BranchPickerBranch[]>([])

  const fetchBranchesSearchData = useCallback(
    async (query: string) => {
      const repoNameWithOwnerParts = repoNameWithOwner.split('/')
      const owner = repoNameWithOwnerParts[0]
      const repo = repoNameWithOwnerParts[1]
      if (!owner || !repo) {
        return
      }
      const repoBranches = await searchBranchesQuery(relayEnvironment, {
        owner,
        repo,
        query,
      })

      setSearchResults(repoBranches)
      setSearchLoading(false)
    },
    [relayEnvironment, repoNameWithOwner],
  )

  const fetchPullRequestsSearchData = useCallback(
    (searchQuery: string) => {
      if (!repoNameWithOwner) return

      setSearchLoading(true)
      fetchQuery<PullRequestAndBranchPickerSearchQuery>(
        relayEnvironment,
        PullRequestAndBranchPickerSearchGraphQLQuery,
        getPullRequestSearchQueries(repoNameWithOwner, searchQuery),
      ).subscribe({
        next: (data: PullRequestAndBranchPickerSearchQuery$data) => {
          if (data !== null) {
            setPullRequestsQueryFragmentKey(data)
            setSearchLoading(false)
          }
        },
        error: () => {
          setSearchLoading(false)
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message: ERRORS.couldNotSearchPullRequests,
          })
        },
      })
    },
    [addToast, relayEnvironment, repoNameWithOwner],
  )

  const fetchSearchData = useCallback(
    (query: string) => {
      fetchBranchesSearchData(query)
      fetchPullRequestsSearchData(query)
    },
    [fetchPullRequestsSearchData, fetchBranchesSearchData],
  )

  function onFilter(value: string) {
    setFilter(value)
  }

  const debounceFetchSearchData = useDebounce(
    (nextValue: string) => fetchSearchData(nextValue),
    VALUES.pickerDebounceTime,
  )

  useEffect(() => {
    fetchSearchData(filter)
    // intentionally running this not debounced on repoNameWithOwner changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repoNameWithOwner])

  useEffect(() => {
    debounceFetchSearchData(filter)
  }, [debounceFetchSearchData, filter])

  return (
    <PullRequestAndBranchPickerInternal
      pullRequestItemsKey={pullRequestsQueryFragmentKey}
      branches={searchResults}
      initialSelectedPrs={initialSelectedPrs}
      initialSelectedBranches={initialSelectedBranches}
      onFilter={onFilter}
      loading={searchLoading}
      {...rest}
    />
  )
}

export function PullRequestAndBranchPickerInternal({
  pullRequestItemsKey,
  branches: fetchedBranches,
  // eslint-disable-next-line @eslint-react/no-unstable-default-props
  initialSelectedPrs = [],
  // eslint-disable-next-line @eslint-react/no-unstable-default-props
  initialSelectedBranches = [],
  ...rest
}: PullRequestPickerInternalProps) {
  const fetchedPrData = useFragment<PullRequestAndBranchPickerQuery$key>(
    PullRequestAndBranchPickerQueryFragment,
    pullRequestItemsKey,
  )

  const pullRequestItems = useMemo<PullRequestPickerPullRequest[]>(() => {
    const map = new Map<string, PullRequestPickerPullRequest>()
    const searchedPRs = getLinkedPullRequests(fetchedPrData ?? null)
    for (const spr of searchedPRs) {
      map.set(spr.id, spr)
    }
    for (const ipr of initialSelectedPrs) {
      map.set(ipr.id, ipr)
    }
    return [...map.values()].sort(sortPrsByCreationDate)
  }, [fetchedPrData, initialSelectedPrs])

  const branchItems = useMemo<BranchPickerBranch[]>(() => {
    const map = new Map<string, BranchPickerBranch>()
    for (const searchBranch of fetchedBranches) {
      map.set(searchBranch.id, searchBranch)
    }
    for (const initialBranch of initialSelectedBranches) {
      map.set(initialBranch.id, initialBranch)
    }
    return [...map.values()]
  }, [fetchedBranches, initialSelectedBranches])

  return (
    <PullRequestAndBranchPickerBase
      initialSelectedPrs={initialSelectedPrs}
      initialSelectedBranches={initialSelectedBranches}
      pullRequestItems={pullRequestItems}
      branchItems={branchItems}
      {...rest}
    />
  )
}

export function PullRequestAndBranchPickerBase({
  pullRequestItems,
  branchItems,
  // eslint-disable-next-line @eslint-react/no-unstable-default-props
  initialSelectedPrs = [],
  // eslint-disable-next-line @eslint-react/no-unstable-default-props
  initialSelectedBranches = [],
  loading,
  onFilter,
  anchorElement,
  shortcutsEnabled,
  ...rest
}: PullRequestPickerBaseProps) {
  const responsiveOnNarrowEnabled = isFeatureEnabled('issues_react_select_panel_fullscreen_on_narrow')
  const initialSelectedItems = useMemo(
    () => [...initialSelectedPrs, ...initialSelectedBranches],
    [initialSelectedPrs, initialSelectedBranches],
  )
  const pullRequestPickerRef = useRef<HTMLButtonElement>(null)

  // Not showing the default branch or branches that already have a PR
  const filteredBranchItems = useMemo(() => {
    return branchItems.filter(
      branch => branch.id !== branch.repository.defaultBranchRef?.id && branch.associatedPullRequests.totalCount === 0,
    )
  }, [branchItems])

  const filterItems = useCallback(
    (value: string) => {
      onFilter(value)
    },
    [onFilter],
  )

  const getItemKey = useCallback((item: PullRequestPickerPullRequest | BranchPickerBranch) => item.id, [])

  const groups: ItemGroup[] = useMemo(() => {
    const itemGroups = []
    if (initialSelectedItems.length > 0) {
      itemGroups.push(selectedGroup)
    }
    if (
      branchItems.length + pullRequestItems.length > 0 &&
      pullRequestItems.length + branchItems.length - initialSelectedItems.length > 0
    ) {
      itemGroups.push(suggestionsGroup)
    }
    return itemGroups
  }, [branchItems.length, initialSelectedItems.length, pullRequestItems.length])

  const convertToItemProps = useCallback(
    (
      item: PullRequestPickerPullRequest | BranchPickerBranch,
    ): ExtendedItemProps<PullRequestPickerPullRequest | BranchPickerBranch> => {
      const isPr = item.__typename === 'PullRequest'
      const displayName = isPr ? item.title : item.name
      const displaySubtitle = isPr ? `${item.repository.nameWithOwner}#${item.number}` : LABELS.noPullRequest
      const icon = isPr ? (
        <Octicon icon={getPrIcon(item)} size={16} sx={{path: {fill: getPrIconColor(item)}}} />
      ) : (
        <Octicon icon={GitBranchIcon} size={16} />
      )

      return {
        // this is a hack to make sure that we are using the prop
        id: `${item.id}`,
        groupId: [...initialSelectedBranches, ...initialSelectedPrs].find(r => r.id === item.id)
          ? selectedGroup.groupId
          : suggestionsGroup.groupId,
        children: (
          <div className={styles.pullRequestItemContainer}>
            <span>{displayName}</span>
            <span className={styles.subtitleText}>{displaySubtitle}</span>
          </div>
        ),
        source: item,
        leadingVisual: () => icon,
        className: styles.Item,
      }
    },
    [initialSelectedBranches, initialSelectedPrs],
  )

  const renderAnchor = useCallback(
    ({...anchorProps}: React.HTMLAttributes<HTMLElement>) => {
      if (anchorElement) {
        return anchorElement(anchorProps)
      }

      return (
        <Button
          leadingVisual={GitPullRequestIcon}
          trailingVisual={TriangleDownIcon}
          {...anchorProps}
          aria-labelledby="pr-picker-label"
          ref={pullRequestPickerRef}
        >
          {initialSelectedItems.length > 0
            ? LABELS.getNumberOfSelectedPrsLabel(initialSelectedItems.length)
            : LABELS.selectPr}
        </Button>
      )
    },
    [anchorElement, initialSelectedItems.length],
  )

  const items = useMemo(() => {
    return [...pullRequestItems, ...filteredBranchItems]
  }, [pullRequestItems, filteredBranchItems])

  const showNoMatchMessage = useMemo(() => items.length === 0, [items.length])

  return (
    <div className={styles.pickerContainer}>
      <ItemPicker
        loading={loading}
        items={[...pullRequestItems, ...filteredBranchItems]}
        initialSelectedItems={initialSelectedItems}
        groups={groups}
        filterItems={filterItems}
        getItemKey={getItemKey}
        convertToItemProps={convertToItemProps}
        placeholderText={LABELS.searchPr}
        selectionVariant="multiple"
        selectPanelRef={pullRequestPickerRef}
        renderAnchor={renderAnchor}
        width="medium"
        resultListAriaLabel={'Pull request results'}
        height="large"
        improvedNoMatchAccessibility={showNoMatchMessage}
        noMatchMessage={
          showNoMatchMessage
            ? {
                title: 'No pull requests or branches were found',
                variant: 'empty',
                body: 'Try searching with a different query for results.',
              }
            : undefined
        }
        {...rest}
        responsiveOnNarrow={responsiveOnNarrowEnabled}
      />
    </div>
  )
}

function getLinkedPullRequests(data: PullRequestAndBranchPickerQuery$data | null): PullRequestPickerPullRequest[] {
  if (!data) return []

  if (data.resource) {
    // eslint-disable-next-line no-restricted-syntax
    const prData = readInlineData<PullRequestAndBranchPickerPullRequest$key>(
      PullRequestAndBranchPickerPullRequestFragment,
      data.resource,
    )

    if (prData) {
      return [prData]
    }
  }

  const {commenters, mentions, assignee, author, open} = data

  const allPrs = [
    ...(commenters?.nodes || []),
    ...(mentions?.nodes || []),
    ...(assignee?.nodes || []),
    ...(author?.nodes || []),
    ...(open?.nodes || []),
  ]

  const fetchedPrsMap = new Map<string, PullRequestPickerPullRequest>()

  for (const pr of allPrs) {
    if (pr !== undefined) {
      // eslint-disable-next-line no-restricted-syntax
      const prData = readInlineData<PullRequestAndBranchPickerPullRequest$key>(
        PullRequestAndBranchPickerPullRequestFragment,
        pr,
      )
      if (prData) {
        fetchedPrsMap.set(prData.id, prData)
      }
    }
  }

  return Array.from(fetchedPrsMap.values())
}

function sortPrsByCreationDate(first: PullRequestPickerPullRequest, second: PullRequestPickerPullRequest) {
  const firstDate = new Date(first.createdAt)
  const secondDate = new Date(second.createdAt)

  if (firstDate < secondDate) {
    return 1
  } else if (firstDate > secondDate) {
    return -1
  } else {
    return 0
  }
}

const getPullRequestSearchQueries = (repoName: string, query = '') => {
  const sharedQuery = `is:pr repo:${repoName}`
  const trimmedQuery = query.trim()
  const queryIsUrl = maybePullRequestUrl(trimmedQuery)
  const queryWithSort = trimmedQuery.length > 0 ? `${trimmedQuery} sort:created-desc` : 'sort:created-desc'
  const modifiedQuery = ` ${queryWithSort}`

  return {
    commenters: `${sharedQuery} commenter:@me${modifiedQuery}`,
    mentions: `${sharedQuery} mentions:@me${modifiedQuery}`,
    assignee: `${sharedQuery} assignee:@me${modifiedQuery}`,
    author: `${sharedQuery} author:@me${modifiedQuery}`,
    open: `${sharedQuery} state:open${modifiedQuery}`,
    resource: queryIsUrl ? encodeURI(trimmedQuery) : '',
    queryIsUrl,
  }
}

function getPrIconColor(pullRequest: PullRequestPickerPullRequest) {
  const item = PullRequestStateIcons[getPrState(pullRequest.isDraft, pullRequest.isInMergeQueue, pullRequest.state)]
  return item.color
}

function getPrIcon(pullRequest: PullRequestPickerPullRequest) {
  const item = PullRequestStateIcons[getPrState(pullRequest.isDraft, pullRequest.isInMergeQueue, pullRequest.state)]
  return item.icon
}

export const getPrState = (isDraft: boolean, isInMergeQueue: boolean, state: PullRequestState) => {
  if (isDraft && state === 'OPEN') {
    return 'DRAFT'
  } else if (isInMergeQueue) {
    return 'IN_MERGE_QUEUE'
  } else if (state === 'CLOSED' || state === 'MERGED' || state === 'OPEN') {
    return state
  }

  throw new Error(`Unknown pull request state: ${state}`)
}
